import React from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff } from "@/components/icons";
import { requestRegistrationSms, confirmRegistration, fetchAuthProviders, type PhoneRegistrationMode } from "@/lib/api";
import { driverPortalLoginHref, handOffToDriverPortal, safeDriverPortalReturn } from "@/lib/driverPortalHandoff";

/**
 * Phone self-registration for platform drivers.
 *
 * Step 1: enter a phone → a code is texted. Step 2: enter the code + a password →
 * the account (and its driver profile) is created, any pending organization
 * invitations for that phone are consumed, and the driver is handed over to the
 * driver portal — to the safe `/charge/...` page in `?next=` (e.g. the charger they
 * came from), else the portal account page. When the account was created but the
 * browser could not be signed in, or joining the inviting organization failed, the
 * page stays put and says so, with an explicit way on. In `invite_only` mode an un-invited phone is told to ask a
 * manager; in `open` mode anyone may register.
 *
 * The whole flow sits behind the server's registration mode (off by default).
 * We ask /api/auth/providers up front so a deep link into /register (from an
 * invite SMS sent before the flag was flipped, or a bookmark) explains itself
 * instead of letting the user fill in a form that can only 404 on submit.
 */
type Outcome = { joinFailed: boolean; signedIn: boolean };

export function RegisterPage() {
  const [params] = useSearchParams();
  const next = params.get("next");
  const [available, setAvailable] = React.useState<boolean | null>(null);
  const [mode, setMode] = React.useState<PhoneRegistrationMode | null>(null);
  const [step, setStep] = React.useState<1 | 2>(1);
  const [phone, setPhone] = React.useState("");
  const [code, setCode] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [username, setUsername] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [outcome, setOutcome] = React.useState<Outcome | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    fetchAuthProviders()
      // The field is optional on older builds; absent means "not offered".
      .then((p) => {
        if (cancelled) return;
        setAvailable(Boolean(p.phone_registration));
        setMode(p.phone_registration_mode ?? null);
      })
      // A failed probe must not lock people out of a working flow — fall back to
      // showing the form and let the endpoint itself be authoritative.
      .catch(() => !cancelled && setAvailable(true));
    return () => {
      cancelled = true;
    };
  }, []);

  async function requestCode(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await requestRegistrationSms(phone.trim());
      if (res.status === "sent") {
        toast.success(res.message || "Code sent by text message.");
        setStep(2);
      } else {
        // exists / not_invited / not_sent — show the backend's guidance.
        toast.error(res.message || "Could not send a code.");
      }
    } catch (err: any) {
      toast.error(err?.message || "Could not send a code.");
    } finally {
      setLoading(false);
    }
  }

  async function confirm(e: React.FormEvent) {
    e.preventDefault();
    if (outcome) return; // the account exists; never submit it twice
    setLoading(true);
    try {
      const res = await confirmRegistration({
        phone: phone.trim(),
        code: code.trim(),
        password,
        username: username.trim() || undefined,
      });
      setCode("");
      setPassword("");
      const joinFailed = res.invitation_status === "failed";
      if (res.logged_in && !joinFailed) {
        toast.success(
          res.joined_organizations?.length
            ? "Account created — you've joined your organization."
            : "Account created.",
        );
        handOffToDriverPortal(next);
        return;
      }
      // The account exists but something needs the person's attention — say so on the
      // page and let them continue explicitly, instead of navigating the message away.
      setOutcome({ joinFailed, signedIn: res.logged_in });
    } catch (err: any) {
      toast.error(err?.message || "Could not complete registration.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-zinc-50 px-4 py-12 text-zinc-950 dark:bg-black dark:text-zinc-50">
      <div className="mx-auto w-full max-w-md">
        <Card>
          <CardHeader>
            <CardTitle>{outcome ? "Account created" : "Create your account"}</CardTitle>
            <CardDescription>
              {outcome
                ? "Your account is ready."
                : mode === "open"
                ? "Sign up with your phone number to start charging. If an organization invited you, use that number and you'll join it automatically."
                : "Registration is by invitation. Enter the phone number your organization manager invited."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {outcome ? (
              <RegistrationOutcome outcome={outcome} next={next} />
            ) : available === null ? (
              <div className="py-6 text-center text-sm text-zinc-500">Loading…</div>
            ) : !available ? (
              <div className="space-y-3 text-sm">
                <p>
                  Self-registration is not available on this server yet.
                </p>
                <p className="text-zinc-500 dark:text-zinc-400">
                  If your organization invited you, your invitation is still on
                  file — ask your organization manager to create your account, or
                  to let you know when registration opens.
                </p>
              </div>
            ) : step === 1 ? (
              <form onSubmit={requestCode} className="space-y-4">
                <div>
                  <Label htmlFor="register-phone">Phone number</Label>
                  <Input
                    id="register-phone"
                    className="mt-2"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="e.g. 555-123-4567"
                    autoFocus
                  />
                </div>
                <Button className="w-full" type="submit" disabled={loading || phone.trim().length < 5}>
                  {loading ? "Sending…" : "Send code"}
                </Button>
              </form>
            ) : (
              <form onSubmit={confirm} className="space-y-4">
                <div>
                  <Label htmlFor="register-code">Verification code</Label>
                  <Input
                    id="register-code"
                    className="mt-2"
                    inputMode="numeric"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="6-digit code"
                    autoFocus
                  />
                </div>
                <div>
                  <Label htmlFor="register-username">Username (optional)</Label>
                  <Input
                    id="register-username"
                    className="mt-2"
                    autoComplete="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Defaults to your phone number"
                  />
                </div>
                <div>
                  <Label htmlFor="register-password">Password (min 10 characters)</Label>
                  <div className="relative mt-2">
                    <Input
                      id="register-password"
                      className="pr-10"
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <Button className="w-full" type="submit" disabled={loading || code.trim().length < 4 || password.length < 10}>
                  {loading ? "Creating…" : "Create account"}
                </Button>
                <button
                  type="button"
                  className="w-full text-center text-xs text-zinc-500 underline hover:text-zinc-700 dark:text-zinc-400"
                  onClick={() => setStep(1)}
                >
                  Use a different phone number
                </button>
              </form>
            )}

            {!outcome && (
              <div className="mt-4 text-center">
                {/* Drivers sign in on the portal (another SPA), returning where they came from. */}
                <a
                  href={driverPortalLoginHref(next)}
                  className="text-xs text-zinc-500 underline hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200"
                >
                  Already have an account? Sign in
                </a>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/** The account exists; what still needs the person, and the one way on. */
function RegistrationOutcome({ outcome, next }: { outcome: Outcome; next: string | null }) {
  return (
    <div className="space-y-4 text-sm">
      {outcome.joinFailed && (
        <div
          role="alert"
          className="rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100"
        >
          <p className="font-medium">Joining your organization did not complete.</p>
          <p className="mt-1">
            Your account was created, but you are not a member of the organization that
            invited you yet, so its rates and access will not apply. Ask your organization
            manager to re-send the invitation.
          </p>
        </div>
      )}
      {outcome.signedIn ? (
        <Button asChild className="w-full">
          <a href={safeDriverPortalReturn(next)}>Continue</a>
        </Button>
      ) : (
        <>
          <p role="status">
            We could not sign you in automatically. Sign in with your phone number and the
            password you just chose.
          </p>
          <Button asChild className="w-full">
            <a href={driverPortalLoginHref(next)}>Sign in</a>
          </Button>
        </>
      )}
    </div>
  );
}
